import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Modal } from 'antd';
import {
  faCheckCircle,
  faCodeBranch,
  faCopy,
  faShareAlt,
  faTimesCircle,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { organisationSlug as primaryOrgSlug, restrictSharedSnapshotsCreation } from 'settings';
import { waitForStoreUpdate } from '@utils';
import IconButton from '@components/IconButton';
import confirmDuplicateModal from '@pathways/cards/Pathways/PathwayList/confirmDuplicateModal';
import {
  duplicatePathway,
  sharePathwaySnapshot,
  publishPathways,
  unpublishPathways,
  unsharePathwaySnapshot,
  deletePathway,
  createPathwaySnapshot,
} from '@pathways/redux/pathways/actions';
import { selectPathway, selectPathwaySnapshots } from '@pathways/redux/pathways/reducers';
import EditButton from '@components/EditButton';
import WrappedCreateSnapshotModal from '@pathways/cards/Pathways/PathwayDetails/createSnapshotModal';
import { selectOrganisationSlug } from '@organisation/redux/selectors';
import SharingSettingsModal from '@pathways/cards/Pathways/PathwayDetails/sharingSettingsModal';

export const CopyPathway = ({ pathway, disabled }) => {
  const { t } = useTranslation();
  const store = useStore();
  const dispatch = useDispatch();

  const openModal = useCallback(() => {
    const onOk = () => {
      dispatch(duplicatePathway(pathway.id));
      return waitForStoreUpdate(store, state => state.pathways.duplicating);
    };

    confirmDuplicateModal(t, onOk);
  }, [dispatch, pathway.id, store, t]);

  return (
    <IconButton
      disabled={disabled}
      icon={faCopy}
      key="copy-pathway"
      tooltip={t('common:buttons.copy')}
      onClick={openModal}
    />
  );
};

export const ShareSnapshot = ({ parentPathwayId, pathway, disabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const orgSlug = useSelector(selectOrganisationSlug());

  if (restrictSharedSnapshotsCreation && orgSlug !== primaryOrgSlug) {
    return null;
  }

  if (orgSlug === primaryOrgSlug) {
    return (
      <SharingSettings parentPathwayId={parentPathwayId} pathway={pathway} disabled={disabled} />
    );
  }

  const showShareModal = () => {
    if (pathway?.isSharedSnapshot) {
      // Unshare modal
      Modal.confirm({
        title: t('pathways:ProcedurePathways.details.modals.confirmUnshare', {
          pathwayName: pathway?.name,
          snapshotName: pathway?.snapshotName,
        }),
        okText: t('common:buttons.unshareSnapshot'),
        cancelText: t('common:buttons.cancel'),
        onOk: () => {
          dispatch(unsharePathwaySnapshot(parentPathwayId, pathway.id));
          return false;
        },
      });
    } else {
      Modal.confirm({
        title: t('pathways:ProcedurePathways.details.modals.confirmShare', {
          pathwayName: pathway?.name,
          snapshotName: pathway?.snapshotName,
        }),
        okText: t('common:buttons.shareSnapshot'),
        cancelText: t('common:buttons.cancel'),
        onOk: () => {
          dispatch(sharePathwaySnapshot(parentPathwayId, pathway.id));
          return false;
        },
      });
    }
  };

  return (
    <IconButton
      disabled={disabled}
      icon={faShareAlt}
      key="share-snapshot"
      tooltip={
        pathway?.isSharedSnapshot
          ? t('common:buttons.unshareSnapshot')
          : t('common:buttons.shareSnapshot')
      }
      onClick={showShareModal}
    />
  );
};

const SharingSettings = ({ parentPathwayId, pathway, disabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [visible, setVisible] = useState(false);

  const snapshots = useSelector(selectPathwaySnapshots(parentPathwayId));
  const currentSharedSnapshot = snapshots.find(snap => snap.isSharedSnapshot);

  const handleSetSharingSettings = useCallback(
    (share, shareWith) => {
      if (parentPathwayId && pathway?.id) {
        if (share) {
          dispatch(sharePathwaySnapshot(parentPathwayId, pathway.id, shareWith));
        } else {
          dispatch(unsharePathwaySnapshot(parentPathwayId, pathway.id));
        }
      }
      setVisible(false);
    },
    [parentPathwayId, pathway, dispatch],
  );

  return (
    <>
      <IconButton
        disabled={disabled}
        icon={faShareAlt}
        key="sharing-settings"
        tooltip={t('common:buttons.sharingSettings')}
        onClick={() => setVisible(true)}
      />
      {visible && (
        <SharingSettingsModal
          t={t}
          onCancel={() => setVisible(false)}
          onOk={handleSetSharingSettings}
          visible={visible}
          currentSharedSnapshot={currentSharedSnapshot}
          isCurrentlyShared={pathway.isSharedSnapshot}
        />
      )}
    </>
  );
};

export const PublishPathway = ({ pathway, disabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();

  const showModal = useCallback(() => {
    Modal.confirm({
      title: pathway?.isActive
        ? t('pathways:ProcedurePathways.details.modals.confirmUnpublish')
        : t('pathways:ProcedurePathways.details.modals.confirmPublish'),
      okText: pathway?.isActive ? t('common:buttons.unpublish') : t('common:buttons.publish'),
      cancelText: t('common:buttons.cancel'),
      onOk: () => {
        pathway?.isActive
          ? dispatch(unpublishPathways([pathway?.id]))
          : dispatch(publishPathways([pathway?.id]));
        return waitForStoreUpdate(store, state => selectPathway(state, pathway?.id));
      },
    });
  }, [dispatch, pathway, store, t]);

  return (
    <IconButton
      disabled={disabled}
      key="publish-pathway"
      icon={pathway?.isActive ? faTimesCircle : faCheckCircle}
      tooltip={pathway?.isActive ? t('common:buttons.unpublish') : t('common:buttons.publish')}
      onClick={showModal}
    />
  );
};

export const DeletePathway = ({ pathway, disabled }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const store = useStore();

  const showModal = useCallback(() => {
    Modal.confirm({
      title: t('pathways:ProcedurePathways.details.modals.confirmDelete', {
        pathwayName: pathway ? pathway.name : '',
      }),
      okText: t('common:buttons.delete'),
      cancelText: t('common:buttons.cancel'),
      onOk: () => {
        dispatch(deletePathway(pathway?.id));
        return waitForStoreUpdate(store, state => selectPathway(state, pathway?.id));
      },
    });
  }, [dispatch, pathway, store, t]);

  return (
    <IconButton
      disabled={disabled}
      icon={faTrashAlt}
      key="delete-pathway"
      tooltip={t('common:buttons.delete')}
      onClick={showModal}
    />
  );
};

export const CreateSnapshot = ({ pathway, disabled, currentSharedSnapshot }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [visible, setVisible] = useState(false);
  const orgSlug = useSelector(selectOrganisationSlug());

  const handleCreateSnapshot = useCallback(
    (values, sharedWith) => {
      dispatch(
        createPathwaySnapshot(
          pathway.id,
          {
            name: values.name,
            description: values.description,
          },
          values.makeShared,
          sharedWith,
        ),
      );
      setVisible(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, pathway.id],
  );

  return (
    <>
      <IconButton
        disabled={disabled}
        icon={faCodeBranch}
        key="create-snapshot"
        tooltip={t('common:buttons.createSnapshot')}
        onClick={() => setVisible(true)}
      />
      <WrappedCreateSnapshotModal
        t={t}
        onCancel={() => setVisible(false)}
        onOk={handleCreateSnapshot}
        currentSharedSnapshot={currentSharedSnapshot}
        visible={visible}
        primaryOrgSlug={primaryOrgSlug}
        orgSlug={orgSlug}
      />
    </>
  );
};

export const EditPathway = ({ disabled }) => {
  const history = useHistory();

  return (
    <EditButton
      disabled={disabled}
      key="edit-pathway"
      onClick={() => {
        history.push(`${history.location.pathname}/edit`);
      }}
    />
  );
};
